import type {
  BarListBlock,
  CardChild,
  ChartBlock,
  MetricBlock,
  NoticePart,
  ResolvedChannels,
  TableBlock,
  TableRow,
  ToolCallStage,
  TurnError,
  WidgetChannels,
  WidgetEncoding,
  WidgetEventData,
  WidgetKind,
  WidgetPart
} from '../types'

/*
 * Turns a `widget` event into the card block the drawer renders. The model's `encoding` names, per channel, where
 * in `data.structuredContent` the channel's values are; the kinds and channels mirror RENDERABLE_WIDGETS of
 * `@sample/agent-chat-contract`, which the service offers to the model on every turn.
 */

const SERIES_CHANNELS: WidgetChannels = {
  categories: { type: 'string[]' },
  series: { type: 'array' },
  series_label: { type: 'string', of: 'series' },
  series_values: { type: 'number[]', of: 'series' },
  series_unit: { type: 'string', of: 'series', required: false }
}

export const WIDGET_KINDS: Readonly<Record<WidgetKind, WidgetChannels>> = {
  'chart.bar': SERIES_CHANNELS,
  'chart.line': SERIES_CHANNELS,
  'chart.bar_horizontal': {
    rows: { type: 'array' },
    label: { type: 'string', of: 'rows' },
    value: { type: 'number', of: 'rows' },
    share: { type: 'number', of: 'rows', required: false },
    unit: { type: 'string', of: 'rows', required: false }
  },
  metric: {
    value: { type: 'number' },
    previous: { type: 'number', required: false },
    unit: { type: 'string', required: false }
  },
  table: {
    rows: { type: 'array' },
    columns: { type: 'scalar', of: 'rows', many: true }
  }
}

const isWidgetKind = (kind: string): kind is WidgetKind =>
  Object.hasOwn(WIDGET_KINDS, kind)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object'

/** The value an RFC 6901 JSON Pointer addresses in `data`, e.g. valueAt({ a: [{ b: 1 }] }, '/a/0/b') → 1. */
export function valueAt(data: unknown, pointer: unknown): unknown {
  if (typeof pointer !== 'string') return undefined
  if (pointer === '') return data
  if (!pointer.startsWith('/')) return undefined
  return pointer
    .slice(1)
    .split('/')
    .map((token) => token.replaceAll('~1', '/').replaceAll('~0', '~'))
    .reduce<unknown>(
      (current, token) => (isRecord(current) ? current[token] : undefined),
      data
    )
}

/*
 * The channels resolved by name. A channel without `of` is a pointer into the data; one with `of` is a property read
 * from every item of that parent array, e.g. { rows: '/branches', label: 'name' } → label: ['Mitte', 'Kreuzberg'];
 * one with `many` is a list of property names, e.g. the columns of a table.
 */
export function resolveEncoding(
  channels: WidgetChannels,
  encoding: WidgetEncoding,
  data: unknown
): ResolvedChannels {
  return Object.fromEntries(
    Object.entries(channels)
      .filter(([name]) => encoding[name] !== undefined)
      .map(([name, channel]): [string, unknown] => {
        const value = encoding[name]
        if (!channel.of) return [name, valueAt(data, value)]
        if (channel.many) return [name, [value].flat()]
        const items = valueAt(data, encoding[channel.of])
        return [
          name,
          Array.isArray(items)
            ? items.map((item: unknown) =>
                isRecord(item) && typeof value === 'string'
                  ? item[value]
                  : undefined
              )
            : undefined
        ]
      })
  )
}

/* drawable() has rejected missing entries of required channels; a null entry counts as 0 */
const asNumbers = (value: unknown): number[] =>
  Array.isArray(value) ? value.map((entry: unknown) => Number(entry ?? 0)) : []

const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : []

/* the entry at `index` of a resolved array channel as a string, e.g. a series' label; undefined when it is missing */
const stringAt = (value: unknown, index: number): string | undefined => {
  const entry: unknown = Array.isArray(value) ? value[index] : undefined
  return entry === undefined || entry === null ? undefined : String(entry)
}

/* the dashboard home's diff formula, so the chat shows the change the home widgets show: (1842, 1615) → '12.32' */
export function getDiff(value: number, previous: number | undefined): string {
  if (!value || !previous) return ''
  return (100 - (previous * 100) / value).toFixed(2)
}

/* 'reorder_level' → 'Reorder level', 'reorderLevel' → 'Reorder level' */
export const humanize = (name: unknown): string => {
  const spaced = String(name)
    .replaceAll(/[_-]+/g, ' ')
    .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/* the first series is drawn in the current period's colour, the second in the previous one's; chart.vue draws two */
const MAX_SERIES = 2

const seriesChild = (
  id: string,
  kind: WidgetKind,
  values: ResolvedChannels
): ChartBlock => ({
  id,
  type: 'chart',
  chartType: kind === 'chart.line' ? 'line' : 'bar',
  graph: {
    axisLabels: asArray(values.categories).map(String),
    series: asArray(values.series_values).map((data, index) => ({
      id: `${index}`,
      period: index === 0 ? 'current' : 'previous',
      label: stringAt(values.series_label, index),
      data: asNumbers(data),
      unit: stringAt(values.series_unit, index)
    }))
  }
})

/* without a share channel a row's share is its part of the rows shown, as in the sample dashboard */
const barListChild = (
  id: string,
  kind: WidgetKind,
  values: ResolvedChannels
): BarListBlock => {
  const rowValues = asNumbers(values.value)
  const total = rowValues.reduce((sum, value) => sum + value, 0)
  const shares = values.share
    ? asNumbers(values.share)
    : rowValues.map((value) =>
        total ? Math.round((value / total) * 10000) / 100 : 0
      )
  return {
    id,
    type: 'bar_list',
    unit: stringAt(values.unit, 0),
    total,
    rows: asArray(values.label).map((label, index) => ({
      id: `${index}`,
      label: label === null || label === undefined ? '–' : String(label),
      value: rowValues[index] ?? 0,
      share: shares[index] ?? 0,
      resolved: true
    }))
  }
}

const metricChild = (
  id: string,
  kind: WidgetKind,
  values: ResolvedChannels
): MetricBlock => {
  const value = Number(values.value ?? 0)
  const previous =
    typeof values.previous === 'number' ? values.previous : undefined
  return {
    id,
    type: 'metric',
    value,
    previous,
    unit: typeof values.unit === 'string' ? values.unit : undefined,
    diffPercent: getDiff(value, previous)
  }
}

const tableChild = (
  id: string,
  kind: WidgetKind,
  values: ResolvedChannels
): TableBlock => ({
  id,
  type: 'table',
  columns: asArray(values.columns).map((key) => ({
    key: String(key),
    label: humanize(key)
  })),
  /* a row that is not an object has no cells; the table shows '–' for each column */
  rows: asArray(values.rows).map((row): TableRow => (isRecord(row) ? row : {}))
})

const CHILDREN: Readonly<
  Record<
    WidgetKind,
    (id: string, kind: WidgetKind, values: ResolvedChannels) => CardChild
  >
> = {
  'chart.bar': seriesChild,
  'chart.line': seriesChild,
  'chart.bar_horizontal': barListChild,
  metric: metricChild,
  table: tableChild
}

/*
 * A required channel that resolves to nothing, an array channel or row channel whose value is not an array, a
 * required row channel with an item that lacks the property, or more series than chart.vue draws, cannot be drawn.
 * E.g. value: 'revenue' over [{ name: 'Mitte', sales: 1200 }] resolves to [undefined] and is not drawn as 0.
 */
const drawable = (
  channels: WidgetChannels,
  values: ResolvedChannels
): boolean =>
  Object.entries(channels).every(([name, channel]) => {
    if (channel.required === false) return true
    const value = values[name]
    if (channel.of) {
      return Array.isArray(value) && value.every((entry) => entry !== undefined)
    }
    if (channel.type === 'array' || channel.type.endsWith('[]')) {
      return Array.isArray(value)
    }
    return value !== undefined && value !== null
  }) &&
  (!Array.isArray(values.series_values) ||
    values.series_values.length <= MAX_SERIES)

/* the widget's own error when it cannot be drawn: an unknown kind, or an encoding that does not resolve */
const widgetError = (widget: WidgetEventData): TurnError => ({
  error_id: widget.widget_id,
  scope: 'widget',
  widget_id: widget.widget_id,
  code: 'WIDGET_UNRENDERABLE',
  message: '',
  retryable: false
})

/**
 * The answer part of a `widget` event: { kind: 'widget', block } with a `card` block, or { kind: 'notice', error }
 * when the drawer cannot draw it. `calls` are the turn's tool_call stages; the card's explain names the call the
 * data came from, else the tool the dataset declares.
 */
export function widgetPart(
  widget: WidgetEventData,
  calls: ToolCallStage[] = []
): WidgetPart | NoticePart {
  const { kind } = widget
  if (!isWidgetKind(kind)) {
    return { kind: 'notice', error: widgetError(widget) }
  }
  const channels = WIDGET_KINDS[kind]
  const values = resolveEncoding(
    channels,
    widget.encoding || {},
    widget.data?.structuredContent
  )
  if (!drawable(channels, values)) {
    return { kind: 'notice', error: widgetError(widget) }
  }

  const source = calls.find(
    (call) => call.call_id === widget.data?.call_ids?.[0]
  )
  return {
    kind: 'widget',
    block: {
      id: widget.widget_id,
      type: 'card',
      title: widget.title,
      explain: {
        tool_name: source?.tool_name ?? widget.data?.schema?.tool,
        tool_args: source?.tool_args
      },
      download: { available: false },
      children: [CHILDREN[kind](`${widget.widget_id}-${kind}`, kind, values)]
    }
  }
}
